// ============================================================================
// Beacon — authentication (Supabase Auth, email + password).
//
// Thin wrapper so the UI never touches the Supabase client directly. Only used
// in CLOUD mode; LOCAL mode has no accounts. A shared team workspace: everyone
// who can sign in sees and works the same deals (see migration 0004).
// ============================================================================

import type { Session } from "@supabase/supabase-js";
import { getSupabase } from "./supabase";

export type { Session };

export async function getSession(): Promise<Session | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  return data.session ?? null;
}

/** Subscribe to sign-in / sign-out. Returns an unsubscribe function. */
export function onAuthChange(cb: (s: Session | null) => void): () => void {
  const sb = getSupabase();
  if (!sb) return () => {};
  const { data } = sb.auth.onAuthStateChange((_e, session) => cb(session));
  return () => data.subscription.unsubscribe();
}

export async function signIn(email: string, password: string): Promise<void> {
  const sb = getSupabase();
  if (!sb) throw new Error("Cloud mode is off.");
  const { error } = await sb.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
}

export async function signUp(email: string, password: string): Promise<{ needsConfirm: boolean }> {
  const sb = getSupabase();
  if (!sb) throw new Error("Cloud mode is off.");
  const { data, error } = await sb.auth.signUp({ email, password });
  if (error) throw new Error(error.message);
  // If email confirmations are on, there's no active session yet.
  return { needsConfirm: !data.session };
}

export async function signOut(): Promise<void> {
  const sb = getSupabase();
  if (sb) await sb.auth.signOut();
}
